import React from "react";
import { getColumnStyle } from "./utils";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";
import type { BaseEntity } from "./types";

/**
 * Extract and organize state from the setup result.
 * This includes destructuring state values and creating memoized handlers.
 */
export function extractSimpleTableState<TData extends BaseEntity = BaseEntity>(
  setup: SimpleTableSetupResult<TData>
) {
  const { state } = setup;

  // Extract state values for easier access
  const {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    sortColumns,
    setSortColumns,
    selectedItems,
    setSelectedItems,
    showFloatingBulkActions,
    setShowFloatingBulkActions,
    columnWidths,
    setColumnWidths,
    columnOrder,
    setColumnOrder,
    draggedColumn,
    setDraggedColumn,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
  } = state;

  // NOTE: this used to also return `handleSelectAll` / `handleBulkAction`
  // placeholders that did nothing ("actual implementation in callbacks") and a
  // second copy of the scroll-sync handlers. Nothing read any of the four —
  // `buildStateSlice` and `createSimpleTableCallbackWrappers` take only the state
  // values and setters — so they were removed with #2625. The real ones come
  // from `useSimpleTableCallbacks` / `SimpleTable`.

  // Get column style callback (expands column when its cell is being edited)
  const getColumnStyleCallback = React.useCallback(
    (fieldId: string, fieldConfig: any) =>
      getColumnStyle(fieldId, fieldConfig, columnWidths, editingCell),
    [columnWidths, editingCell]
  );

  return {
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    sortColumns,
    setSortColumns,
    selectedItems,
    setSelectedItems,
    showFloatingBulkActions,
    setShowFloatingBulkActions,
    columnWidths,
    setColumnWidths,
    columnOrder,
    setColumnOrder,
    draggedColumn,
    setDraggedColumn,
    editingCell,
    setEditingCell,
    editValue,
    setEditValue,
    effectiveCurrentPage,
    effectiveSetCurrentPage,
    effectivePageSize,
    effectiveSetPageSize,
    getColumnStyleCallback,
  };
}

export type SimpleTableStateSetupResult = ReturnType<typeof extractSimpleTableState>;
